import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CalendarClock, Search, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DateTimeStep } from "@/components/booking/DateTimeStep";
import { api, errMsg, fmtDate, rememberPhone } from "@/lib/api";
import { PHONE, PHONE_DISPLAY } from "@/lib/clinic";

const btn = "h-12 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50";
const STATUS = { CONFIRMED: "bg-mint text-teal", CANCELLED: "bg-red-50 text-red-600", COMPLETED: "bg-slate-100 text-slate-700", PENDING_PAYMENT: "bg-amber-50 text-amber-700" };

const LookupForm = ({ creds, setCreds, onFound }) => {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onFound((await api.post("/manage/lookup", creds)).data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="manage-lookup-form">
      <label className="block text-xs font-medium">Booking reference
        <Input value={creds.booking_ref} onChange={(e) => setCreds({ ...creds, booking_ref: e.target.value.toUpperCase() })} placeholder="SMD-XXXXXX" className="h-11 rounded-xl mt-1 uppercase" data-testid="manage-ref-input" />
      </label>
      <label className="block text-xs font-medium">Mobile number used for booking
        <div className="mt-1 flex h-11 rounded-xl border border-input overflow-hidden focus-within:ring-2 focus-within:ring-ring">
          <span className="px-3 grid place-items-center bg-slate-50 text-sm text-ink-muted border-r">+91</span>
          <input value={creds.patient_phone} onChange={(e) => setCreds({ ...creds, patient_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputMode="numeric" placeholder="10-digit number" className="flex-1 px-3 text-sm outline-none" data-testid="manage-phone-input" />
        </div>
      </label>
      <button type="submit" disabled={busy || creds.booking_ref.length < 4 || creds.patient_phone.length !== 10} className={`${btn} w-full bg-teal text-white hover:bg-teal-dark`} data-testid="manage-lookup-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Find my booking
      </button>
    </form>
  );
};

const BookingCard = ({ b, onReschedule }) => (
  <div data-testid="manage-booking-card">
    <div className="flex items-center justify-between">
      <p className="font-display text-2xl font-semibold text-teal" data-testid="manage-booking-ref">{b.booking_ref}</p>
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[b.status]}`} data-testid="manage-booking-status">{b.status.replace("_", " ")}</span>
    </div>
    <div className="mt-4 rounded-2xl bg-mint/50 border border-mint-border p-4 text-sm space-y-1">
      <p className="font-medium text-ink" data-testid="manage-booking-when">{fmtDate(b.appointment_date, { weekday: "long", day: "numeric", month: "long" })} · {b.slot_label}</p>
      <p className="text-ink-muted">{b.visit_reason_title}</p>
      <p className="text-ink-muted">{b.patient_name}</p>
    </div>
    {b.status === "CANCELLED" && b.cancel_reason === "Cancelled by patient" && (
      <p className="mt-3 text-sm text-ink-muted" data-testid="manage-cancelled-note">This booking is cancelled. The clinic will contact you about your consultation fee.</p>
    )}
    {b.note && b.status === "CONFIRMED" && <p className="mt-3 flex gap-2 text-sm text-amber-700" data-testid="manage-note"><Info className="h-4 w-4 mt-0.5 shrink-0" />{b.note}</p>}
    <div className="mt-5 grid gap-2.5">
      {b.can_reschedule && <button onClick={onReschedule} className={`${btn} bg-teal text-white hover:bg-teal-dark`} data-testid="manage-reschedule-button"><CalendarClock className="h-4 w-4" />Change date &amp; time</button>}
      <a href={`tel:${PHONE}`} className="text-center text-sm text-teal hover:underline" data-testid="manage-call-link">Need help? Call {PHONE_DISPLAY}</a>
    </div>
    {b.can_reschedule && <p className="mt-3 text-[11px] text-ink-muted text-center" data-testid="manage-policy-note">You can reschedule once, up to the day before your visit. Confirmed consultations can't be cancelled.</p>}
  </div>
);

export default function ManageBooking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [creds, setCreds] = useState({ booking_ref: (params.get("ref") || "").toUpperCase(), patient_phone: "" });
  const [b, setB] = useState(null);
  const [mode, setMode] = useState("view");
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const reschedule = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/manage/reschedule", { ...creds, appointment_date: date, slot_time: slot.time });
      rememberPhone(data.id, creds.patient_phone);
      toast.success(`Moved to ${fmtDate(data.appointment_date)} · ${data.slot_label}`);
      navigate(`/confirmation/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e)); setSlot(null); setRefreshKey((k) => k + 1);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-alabaster grain py-10 px-4" data-testid="manage-page">
      <div className="max-w-lg mx-auto reveal">
        <Link to="/" className="flex items-center justify-center gap-2 font-display text-xl text-teal mb-6"><img src="/images/logo.png" alt="" className="h-10 w-10 rounded-full" />Smileton Dental</Link>
        <div className="rounded-[2rem] bg-white border border-mint-border/70 shadow-[0_30px_60px_-30px_rgba(15,76,92,0.45)] p-6 sm:p-8">
          <h1 className="text-3xl font-semibold text-ink mb-1">Manage my booking</h1>
          <p className="text-sm text-ink-muted mb-6">Move your appointment to another day. No account needed.</p>
          {!b ? <LookupForm creds={creds} setCreds={setCreds} onFound={setB} />
            : mode === "reschedule" ? (
              <>
                <DateTimeStep date={date} setDate={setDate} slot={slot} setSlot={setSlot} refreshKey={refreshKey} reasonTitle={`Currently ${fmtDate(b.appointment_date)} · ${b.slot_label}`}
                  title="Pick a new date & time" excludeToday backLabel="Back" onBack={() => setMode("view")} onNext={reschedule} nextLabel={busy ? "Moving…" : undefined} />
              </>
            ) : <BookingCard b={b} onReschedule={() => { setSlot(null); setMode("reschedule"); }} />}
        </div>
        <Link to="/" className="block text-center text-sm text-teal mt-6 hover:underline" data-testid="manage-home-link">Back to Smileton Dental</Link>
      </div>
    </div>
  );
}
